import { findBannerByCode, getCleanBannerCode } from 'utils/promo-banner-utils';
import { useEffect, useMemo, useRef, useState } from 'react';

import { captureMessage } from '@sentry/nextjs';
import ChipList from 'components/widgets/chip/chip-list';
import CommentContainer from 'components/blog/comments/comment-container';
import ContentBanner from 'components/blog/content-banner/content-banner';
import ContentFormBanner from 'components/blog/content-banner/content-form-banner';
import dynamic from 'next/dynamic';
import Image from 'core/image';
import LandingSection from 'components/landing/components/landing-section';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import RandomNoteList from '../random-note-list/random-note-list';
import RelatedNote from 'components/blog/blog-content/related-note';
import Spacer from 'core/spacer';
import styles from 'styles/BlogContent.module.scss';
import TableResumeHeader from 'components/landing/index/table-resume-header';
import { useCulture } from 'context/CultureContext';
import { usePageInfo } from 'context/PageInfoContext';
import { useTranslations } from 'next-intl';

const ShareSocial = dynamic(() => import('components/widgets/share-social'));

function getRandomInt (min = 0, max = 99999) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min);
}

const parseUl = (children) => {
    try {
        return children.map((child) => {
            if (child.name === 'li') {
                let titleDisplay = '';

                const extractText = (nodes) => {
                    return nodes.map((node) => {
                        if (node.type === 'text') {
                            return node.data;
                        }
                        if (node.name === 'strong' || node.name === 'span') {
                            return `<${node.name}>${extractText(node.children)}</${node.name}>`;
                        }
                        if (node.children !== undefined) {
                            return extractText(node.children);
                        }
                        return '';
                    }).join('');
                };

                titleDisplay = extractText(child.children);

                return {
                    title: titleDisplay,
                    id: child.children[0].attribs ? child.children[0].attribs.href : null
                };
            }

            if (child.name === 'ul') {
                return { title: 'ul', id: 'ul', children: parseUl(child.children) };
            }

            return null;
        });
    } catch (error) {
        return null;
    }
};

const renderBannerFromParagraph = (children, bannerList, culture) => {
    try {
        if (
            children !== null &&
            children !== undefined &&
            children.length === 1 &&
            children[0].type === 'text'
        ) {
            const text = children[0].data;
            const bannerPattern = /\[banner:([^\]]+)\]/;
            const bannerMatch = text.match(bannerPattern);

            if (bannerMatch !== null) {
                let code = bannerMatch[1];
                if (code.includes('<') || code.includes('>')) {
                    code = getCleanBannerCode(code);
                }

                const bannerConfig = findBannerByCode(bannerList, code);

                if (bannerConfig !== undefined) {
                    return <ContentFormBanner config={bannerConfig} culture={culture} />;
                }

                return <></>;
            }
        }
    } catch (err) {
        captureMessage(`blog-content: renderBannerFromParagraph failed: ${err?.message || String(err)}`);
        return <></>;
    }
};

export const convertToHtml = (rawContent, imageAlt, bannerList, culture) => {
    const HEIGHT_DEFAULT = '200';
    const WIDTH_DEFAULT = '400';

    const options = {
        replace: ({ name, children, attribs = {} }) => {
            if (name === 'ul') {
                const ulData = parseUl(children);
                if (ulData !== null) {
                    return <TableResumeHeader items={ulData} />;
                }
            } else if (name === 'img' && Object.keys(attribs).length !== 0) {
                const styleAttr = attribs.style;
                let height = HEIGHT_DEFAULT;
                let width = WIDTH_DEFAULT;

                if (styleAttr !== undefined) {
                    const heightRegex = /height:(\d+)px/;
                    const heightMatch = styleAttr.match(heightRegex);
                    height = heightMatch ? heightMatch[1] : HEIGHT_DEFAULT;

                    const widthRegex = /width:(\d+)px/;
                    const widthMatch = styleAttr.match(widthRegex);
                    width = widthMatch ? widthMatch[1] : WIDTH_DEFAULT;
                }

                attribs.width = Number(width);
                attribs.height = Number(height);
                attribs.style = '';

                if ('class' in attribs) {
                    delete attribs.class;
                }

                if (attribs.alt === '') {
                    attribs.alt = imageAlt.replace('[index]', getRandomInt());
                }

                return (
                    <Image {...attribs} className={styles.image} priority />
                );
            } else if (name === 'p') {
                const node = renderBannerFromParagraph(children, bannerList, culture);
                if (node !== null) {
                    return node;
                }
            }
        }
    };

    return parse(rawContent, options);
};

const BlogContent = () => {
    const { pageInfo } = usePageInfo();
    const { culture } = useCulture();
    const t = useTranslations('blog-content');
    const imageAlt = t('image-alt');

    const divRef = useRef(null);
    const shareRef = useRef(null);
    const [showShare, setShowShare] = useState(false);

    const bannerListContent = useMemo(
        () => pageInfo.page_context.content_promo_banner_list || [],
        [pageInfo.page_context.content_promo_banner_list]
    );
    const bannerList = useMemo(
        () => pageInfo.page_context.lateral_promo_banner_list || [],
        [pageInfo.page_context.lateral_promo_banner_list]
    );

    const htmlContent = useMemo(
        () => convertToHtml(
            pageInfo.page_context.content,
            imageAlt,
            bannerListContent,
            culture
        ),
        [pageInfo.page_context.content, imageAlt, bannerListContent, culture]
    );

    // Lazy load ShareSocial when its container approaches the viewport
    useEffect(() => {
        const el = shareRef.current;
        if (!el) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShowShare(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Build lateral column data from DOM height measurement
    const [lateralColumnData, setLateralColumnData] = useState([]);

    useEffect(() => {
        if (!divRef.current) {
            return;
        }

        const height = divRef.current.clientHeight;

        let maxNotes = 4;
        if (height < 800) {
            maxNotes = 2;
        } else if (height < 2000) {
            maxNotes = 4;
        } else if (height < 3000) {
            maxNotes = 6;
        } else if (height < 4000) {
            maxNotes = 8;
        } else if (height < 6000) {
            maxNotes = 12;
        } else {
            maxNotes = 15;
        }

        const relatedNotes = pageInfo.page_context.page_related_notes;
        const notes = relatedNotes
            .slice(0, maxNotes)
            .sort((a, b) => (b.image !== null) - (a.image !== null));

        if (!bannerList?.length) {
            setLateralColumnData(
                notes.map((note) => ({ type: 'note', note }))
            );
            return;
        }

        const result = [];
        let bannerIdx = 0;

        notes.forEach((note, i) => {
            if (i === 0 || (i + 1) % 4 === 0) {
                result.push({
                    type: 'banner',
                    config: bannerList[bannerIdx % bannerList.length].config
                });
                bannerIdx++;
            }
            result.push({ type: 'note', note });
        });

        setLateralColumnData(result);
    }, [pageInfo.page_context.page_related_notes, bannerList]);

    const tagItemList = useMemo(
        () => (pageInfo.page_context.tags || []).map((tag) => ({
            href: `/blog/?q=tag:${tag}`,
            text: `#${tag}`
        })),
        [pageInfo.page_context.tags]
    );

    return (
        <LandingSection disableBottomPadding disableTopPadding disableGradient>
            <div className={styles.container}>
                <div className={styles.content}>
                    <div className={styles.content_body} ref={divRef}>
                        {htmlContent}
                    </div>
                    <Spacer />
                    <ChipList itemList={tagItemList} />
                    <div ref={shareRef}>
                        {showShare && (
                            <div className={styles.share_container}>
                                <ShareSocial
                                    title={pageInfo.page_context.title}
                                    url={pageInfo.page_context.page_full_url}
                                    minorPrice={pageInfo.page_context.page_dinamic_data.minor_price}
                                />
                            </div>
                        )}
                    </div>
                    <RandomNoteList
                        limitNoteInARow={6}
                        mobileLimitInARow={2}
                        randomNoteList={pageInfo.page_context.page_related_notes.slice().reverse()}
                    />
                    <CommentContainer />
                </div>
                <div className={styles.popular_notes}>
                    {lateralColumnData.map((item, i) => {
                        if (item.type === 'banner') {
                            return (
                                <ContentBanner
                                    key={`banner-${i}`}
                                    config={item.config}
                                    bannerType="lateral"
                                    culture={culture}
                                />
                            );
                        }

                        return (
                            <RelatedNote
                                key={`note-${i}`}
                                item={item.note}
                            />
                        );
                    })}
                </div>
            </div>
        </LandingSection>
    );
};

BlogContent.propTypes = {
    rawContent: PropTypes.string,
    relatedNoteList: PropTypes.array,
    bannerList: PropTypes.array
};

export default BlogContent;
